import { getContentfulGqlClient } from '@/contentful/wac-content-gql-client.ts';

import { reshapeCollection } from './reshape';

import type { ConnectorPage, ConnectorQueryEcho, RunConnectorQuery } from './types';

/**
 * Posts an echoed connector query to Contentful GraphQL and reshapes the result.
 *
 * The query string comes from the edgehancer echo, so this module never builds a
 * query and never names a content type. It finds the collection node by shape,
 * the same way the `request` edgehancer does, because the field name depends on
 * the content type.
 *
 * `reshapeCollection` converts the GraphQL items into the Contentful REST entry
 * shape, which is what the gallery cards bind through their `$:fields.…` paths.
 * The server-rendered items already carry that shape, so both sources match.
 */
export const runConnectorQuery: RunConnectorQuery = async (echo) => {
	const result = await getContentfulGqlClient()
		.query<Record<string, unknown>, Record<string, unknown>>(
			echo.query,
			echo.variables as Record<string, unknown>,
		)
		.toPromise();

	// Contentful answers a partially broken link with data PLUS errors. The
	// edgehancer downgrades that to a warning and keeps the data, so do the same:
	// one unpublished link must not empty a gallery page.
	const data = result.data;
	if (!data) {
		throw new Error(
			`WAE connector: no data for the echoed query${
				result.error ? `: ${result.error.message}` : ''
			}`,
		);
	}

	const node = findCollectionNode(data);
	// Contentful returns a `null` item for an entry the token cannot read. Dropping
	// them here keeps a blank card out of the gallery, whose dedupe cannot filter
	// them because a null has no `sys.id`.
	const rawItems = (Array.isArray(node?.items) ? node.items : []).filter(
		(item): item is Record<string, unknown> => typeof item === 'object' && item !== null,
	);

	return {
		items: reshapeCollection(rawItems),
		total: typeof node?.total === 'number' ? node.total : rawItems.length,
		skip: echo.variables.skip ?? 0,
	} satisfies ConnectorPage;
};

/** The first node that looks like a Contentful collection: `{ total?, items[] }`. */
const findCollectionNode = (
	data: Record<string, unknown>,
): { items?: unknown; total?: unknown } | undefined => {
	for (const value of Object.values(data)) {
		if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown }).items)) {
			return value as { items?: unknown; total?: unknown };
		}
	}
	return undefined;
};

/** Advance the echo to the next page. */
export const withSkip = (echo: ConnectorQueryEcho, skip: number): ConnectorQueryEcho => ({
	query: echo.query,
	variables: { ...echo.variables, skip },
});
